import { Fragment, useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { BadgeProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  HStack,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'

import { DisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { Badge } from '~/components/common/Badge'
import { CompactAlert } from '~/components/common/CompactAlert'
import { TABLE_HEADER_CELL_STYLE } from '~/components/common/MasterDataTable'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import { convertAreaLabel } from '~/models/area'
import {
  KOMA_DIVIDER_ROW_CSS,
  isKomaDividerBetween,
} from '~/models/komaDivider'
import { komaNumberTimeRange } from '~/models/komaToTime'
import type {
  BlockExecKind,
  BlockExecResult,
} from '~/pages/SpotBidding/models/blockBidRow'

type Props = {
  results: BlockExecResult[]
}

// design `#block-exec-table` 実測 (getComputedStyle): td 14px/400・badge 14px。
// ecloud Table の size recipe は td を 12px に縮めるため、 fontSize を明示する。
// TH 側は `TABLE_HEADER_CELL_STYLE` (共通の single source) が持つ。
const TD = { fontSize: 'md' } as const

// 種別バッジ (= 通常 / リンク / ループ)。
//
// design `#block-exec-table` が semantic クラス (`badge-neutral` / `badge-info` /
// `badge-warn`) をブロック種別の識別に流用しているため、 それに従って semantic status
// を当てている (= 「ループ = warning」 は重要度としての警告ではなく design 準拠の
// 流用)。
const KIND_BADGE: Record<
  BlockExecKind,
  { labelKey: string; status: BadgeProps['status'] }
> = {
  STANDARD: {
    labelKey: 'page:biddingSpot.blockExec.kind.standard',
    status: 'neutral',
  },
  LINK: { labelKey: 'page:biddingSpot.blockExec.kind.link', status: 'info' },
  LOOP: { labelKey: 'page:biddingSpot.blockExec.kind.loop', status: 'warning' },
}

// 数値セル: 値があれば小数 digits 桁で右寄せ tabular-nums、 undefined は淡色「—」。
const NumCell = ({
  value,
  digits,
}: {
  value?: number
  digits: number
}): React.ReactElement => (
  <TableCell
    color={value === undefined ? 'Text.lowEmphasis' : undefined}
    textAlign="right"
    css={{ fontVariantNumeric: 'tabular-nums' }}
  >
    {value === undefined ? '—' : value.toFixed(digits)}
  </TableCell>
)

/**
 * 約定結果タブの「約定結果（ブロック入札）」 親子テーブル (AC-BSP-037)。
 *
 * design HTML `design/bidding/spot.html` `#block-exec-table` 準拠:
 *   - 親行 (種別 / エリア / コマ範囲 / 状態) をクリックで子テーブルを開閉
 *   - 子テーブルは Leg × コマ の約定 (Leg / コマ / 時間帯 / エリア / 売買 / 入札 / 約定 / 約定価格)
 *   - LINK 未約定時は all-or-nothing 説明 alert を子テーブル上部に表示
 */

export const BlockExecutionTable = ({ results }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <Box overflowX="auto">
      <TableRoot>
        <TableHeader
          // 入札一覧 (BlockBidTable) と同じ理由で éclair の TH 規定を当てる
          // (デザイナー指摘 2026-08-27「8080同様、DS Table 適用 (TH)」/
          //  2026-08-31 ブロック入札への展開)。
          css={{ '& th': TABLE_HEADER_CELL_STYLE }}
        >
          <TableRow>
            {/* 開閉トグル列。 TD 側が center なのでヘッダも center に揃える
                (TH の揃えに TD を合わせる規約。 2026-08-27 デザイナー指摘)。 */}
            <TableColumnHeader width="32px" textAlign="center" />
            {/* [design 差異] ステータスは全画面共通でデータ列の先頭に置く
                (2026-08-27 デザイナー判断)。 左端の 32px は開閉トグル (操作) で
                データ列ではないため、 その次を先頭として扱う。
                design (`design/bidding/spot.html` の `#block-exec-table`) は 5 列目
                (コマ範囲 の後ろ = 最終列) だが、 行の状態は他の属性を読む前に
                分かる必要があるため一覧の左端へ統一した。 */}
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.blockExec.column.status')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.blockExec.column.kind')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingSpot.blockExec.column.area')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingSpot.blockExec.column.komaRange')}
            </TableColumnHeader>
            {/* どの発注主体区分のブロックかは受渡契約コードで判別する (AC-BSP-037 は
                発注主体区分で絞らない)。 design `#block-exec-table` (#10970)。 */}
            <TableColumnHeader textAlign="right">
              {t('page:biddingSpot.column.deliveryContractCode')}
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        {/* TD は design `.ecl-table td` の 6px 10px (UI 辞書 table.md §0) */}
        <TableBody>
          {results.map((r) => {
            const isOpen = expanded.has(r.id)
            const kind = KIND_BADGE[r.kind]
            return (
              <Fragment key={r.id}>
                <TableRow cursor="pointer" onClick={() => toggle(r.id)}>
                  <TableCell textAlign="center">
                    {/* 行の開閉標。 他の accordion (入札一覧ヘッダ等) と同じ
                        `DisclosureIcon` を使う。 従来は `▾` を文字で書いて回転させて
                        いたため、 (1) フォント依存で太さ・大きさが他画面の chevron と
                        揃わず、 (2) 8080 が採る**グリフ差し替え** (閉 = chevron-right /
                        開 = chevron-down) ではなく回転になっていた。 */}
                    <DisclosureIcon expanded={isOpen} />
                  </TableCell>
                  <TableCell {...TD} textAlign="center">
                    <Badge status={r.statusBadge}>{t(r.statusLabelKey)}</Badge>
                  </TableCell>
                  <TableCell {...TD}>
                    <Badge status={kind.status}>{t(kind.labelKey)}</Badge>
                  </TableCell>
                  <TableCell {...TD} textAlign="center">
                    {r.areaCodes.map(convertAreaLabel).join(' / ')}
                  </TableCell>
                  <TableCell
                    {...TD}
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {r.komaRangeLabel}
                  </TableCell>
                  <TableCell
                    {...TD}
                    textAlign="right"
                    css={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {r.deliveryContractCode !== undefined &&
                    r.deliveryContractCode !== ''
                      ? r.deliveryContractCode
                      : '—'}
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow>
                    <TableCell
                      padding={3}
                      backgroundColor="Surface.secondary"
                      colSpan={6}
                    >
                      {r.note !== undefined && (
                        <Box marginBottom={2}>
                          <CompactAlert
                            description={r.note.desc}
                            status="info"
                            title={r.note.title}
                          />
                        </Box>
                      )}
                      <Box
                        overflow="hidden"
                        borderWidth="1px"
                        borderColor="Border.lowEmphasis"
                        borderRadius="md"
                      >
                        <TableRoot>
                          {/* 展開した leg 明細も同じ見出し規定に揃える
                              (親表だけ直すと 1 画面内で見出しが 2 種になる)。 */}
                          <TableHeader
                            css={{ '& th': TABLE_HEADER_CELL_STYLE }}
                          >
                            <TableRow>
                              <TableColumnHeader>
                                {t('page:biddingSpot.blockExec.column.leg')}
                              </TableColumnHeader>
                              <TableColumnHeader textAlign="right">
                                {t('page:biddingSpot.blockExec.column.koma')}
                              </TableColumnHeader>
                              <TableColumnHeader>
                                {t(
                                  'page:biddingSpot.blockExec.column.timeBand',
                                )}
                              </TableColumnHeader>
                              <TableColumnHeader textAlign="center">
                                {t('page:biddingSpot.blockExec.column.area')}
                              </TableColumnHeader>
                              <TableColumnHeader textAlign="center">
                                {t('page:biddingSpot.column.side')}
                              </TableColumnHeader>
                              <TableColumnHeader textAlign="right">
                                {t(
                                  'page:biddingSpot.blockExec.column.bidVolume',
                                )}
                              </TableColumnHeader>
                              <TableColumnHeader textAlign="right">
                                {t(
                                  'page:biddingSpot.blockExec.column.execVolume',
                                )}
                              </TableColumnHeader>
                              <TableColumnHeader textAlign="right">
                                {t('page:biddingSpot.blockExec.price.exec')}
                              </TableColumnHeader>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {r.children.map((c, i) => {
                              const next = r.children[i + 1]
                              return (
                                <TableRow
                                  key={`${c.legRoleKey ?? ''}-${c.koma}`}
                                  // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの
                                  // 表の区切り線)。 Leg が変わる所では引かない。
                                  css={
                                    next?.legRoleKey === c.legRoleKey &&
                                    isKomaDividerBetween(c.koma, next?.koma)
                                      ? KOMA_DIVIDER_ROW_CSS
                                      : undefined
                                  }
                                >
                                  <TableCell {...TD}>
                                    {c.legRoleKey !== undefined
                                      ? t(c.legRoleKey)
                                      : '—'}
                                  </TableCell>
                                  <TableCell
                                    {...TD}
                                    textAlign="right"
                                    css={{ fontVariantNumeric: 'tabular-nums' }}
                                  >
                                    {String(c.koma).padStart(2, '0')}
                                  </TableCell>
                                  <TableCell
                                    {...TD}
                                    css={{ fontVariantNumeric: 'tabular-nums' }}
                                  >
                                    {komaNumberTimeRange(c.koma, '〜')}
                                  </TableCell>
                                  <TableCell {...TD} textAlign="center">
                                    {convertAreaLabel(c.areaCode)}
                                  </TableCell>
                                  <TableCell {...TD} textAlign="center">
                                    <TradeSideLabel side={c.direction} />
                                  </TableCell>
                                  <NumCell digits={1} value={c.bidVolumeMw} />
                                  <NumCell digits={1} value={c.execVolumeMw} />
                                  <NumCell
                                    digits={2}
                                    value={c.execPriceYenPerKwh}
                                  />
                                </TableRow>
                              )
                            })}
                          </TableBody>
                        </TableRoot>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </TableRoot>
      {results.length === 0 && (
        <HStack justifyContent="center" paddingY={10}>
          <Text color="Text.lowEmphasis" fontSize="sm">
            {t('page:biddingSpot.blockExec.emptyFiltered')}
          </Text>
        </HStack>
      )}
    </Box>
  )
}
